// The Floor screen's Reservations panel (host / manager on the iPad): the day's
// book with New reservation / Edit / Seat / No-show / Cancel, for a guest on
// the phone or at the door. The same endpoints, availability and form logic as
// the back office (lib/reservationDesk); drawn in the dining design (dark,
// touch targets >= 48 px). Seating opens the table's session (the same status call).
import React, { useMemo, useState } from 'react';
import { api, errorText } from '../lib/api';
import { plural } from '../lib/format';
import { C, FONT_TIGHT } from '../theme/tokens';
import { Btn, Chip, Empty, ErrorNote, IconBtn, Modal, ModalFoot, ModalHead, Skeleton, useToast } from '../components/ui';
import {
  IconAlert, IconArrowLeft, IconCalendar, IconCheck, IconChevronRight, IconClose, IconGift, IconMinus, IconPencil,
  IconPhone, IconPlus, IconShield, IconUsers,
} from '../components/icons';
import {
  COUNTRY_CODES, OCCASIONS, dayLabel, hhmm12, reservationActions, savedText, shiftDay, statusDoneText,
  useReservationDay, useReservationForm,
} from '@/lib/reservationDesk';

const STATUS_CHIP = {
  pending: { bg: 'rgba(245,158,11,.18)', fg: '#FBBF24', label: 'Pending' },
  confirmed: { bg: 'rgba(34,197,94,.14)', fg: '#4ADE80', label: 'Confirmed' },
  seated: { bg: 'rgba(139,92,246,.2)', fg: '#C4B5FD', label: 'Seated' },
  completed: { bg: 'rgba(100,116,139,.25)', fg: '#CBD5E1', label: 'Completed' },
  cancelled: { bg: 'rgba(100,116,139,.25)', fg: '#94A3B8', label: 'Cancelled' },
  no_show: { bg: 'rgba(239,68,68,.18)', fg: '#FCA5A5', label: 'No-show' },
};

const darkInput = {
  height: 52, borderRadius: 12, border: `1px solid ${C.line2}`, background: C.panel2, color: C.textHi,
  padding: '0 14px', fontSize: 16, width: '100%', minWidth: 0, colorScheme: 'dark',
};

function useDiningTmClient() {
  return useMemo(() => ({
    get: (path, query) => api.get(`/table-management${path}`, query),
    post: (path, body) => api.post(`/table-management${path}`, body),
    patch: (path, body) => api.patch(`/table-management${path}`, body || {}),
  }), []);
}

export default function ReservationsPanel({ perms, refreshKey, onClose, onSeated }) {
  const client = useDiningTmClient();
  const toast = useToast();
  const book = useReservationDay(client, { refreshKey });
  const { day, setDay, today, zone, data, error, load, active, closed, guests } = book;
  const [form, setForm] = useState(null); // null | {reservation?}
  const [ask, setAsk] = useState(null); // {r, action}
  const [busyId, setBusyId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const canWrite = perms.can('reservations.write');

  const run = async (r, action) => {
    setAsk(null);
    setBusyId(r.id);
    try {
      await book.setStatus(r, action.status);
      toast.ok(statusDoneText(r, action.status));
      if (action.status === 'seated') onSeated(r);
      load();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusyId(null);
    }
  };
  const onAction = (r, action) => {
    if (action.edit) { setForm({ reservation: r }); return; }
    if (action.ask) { setAsk({ r, action }); return; }
    run(r, action);
  };

  return (
    <div className="c29-fade" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 58, background: 'rgba(0,0,0,.55)', display: 'flex', justifyContent: 'flex-end' }}>
      <div role="dialog" aria-modal="true" aria-label="Reservations" className="c29-sheet"
        style={{ width: 640, maxWidth: '100%', height: '100%', background: C.panel, borderLeft: `1px solid ${C.line2}`, display: 'flex', flexDirection: 'column', boxShadow: '-30px 0 80px rgba(0,0,0,.45)' }}>
        {form ? (
          <ReservationForm client={client} reservation={form.reservation} defaultDate={day || today} today={today} zone={zone}
            onBack={() => setForm(null)}
            onSaved={(answer) => {
              const said = savedText(answer, !!form.reservation);
              if (said.ok) toast.ok(said.text); else toast.error(said.text);
              setForm(null);
              if (answer.reservation.date !== day) setDay(answer.reservation.date); else load();
            }} />
        ) : (
          <>
            <header style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(139,92,246,.16)', color: '#C4B5FD', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><IconCalendar size={22} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="c29-tight" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.textHi }}>Reservations</h2>
                <span style={{ fontSize: 13, color: C.textLo }}>{zone ? `Restaurant time · ${zone}` : 'Restaurant time'}</span>
              </div>
              {canWrite && <Btn kind="orange" height={48} icon={<IconPhone size={18} />} onClick={() => setForm({})}>New reservation</Btn>}
              <IconBtn dark size={48} label="Close reservations" onClick={onClose}><IconClose size={20} /></IconBtn>
            </header>

            <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.line}` }}>
              <IconBtn dark size={48} label="Previous day" onClick={() => day && setDay(shiftDay(day, -1))}><IconArrowLeft size={18} /></IconBtn>
              <input type="date" aria-label="Day" value={day || ''} onChange={(e) => e.target.value && setDay(e.target.value)} style={{ ...darkInput, height: 48, width: 180, flex: 'none' }} />
              <IconBtn dark size={48} label="Next day" onClick={() => day && setDay(shiftDay(day, 1))}><IconChevronRight size={18} /></IconBtn>
              {today && day !== today && <Btn kind="dark" height={48} onClick={() => setDay(today)}>Today</Btn>}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 13, color: C.textMid, textAlign: 'right' }}>
                <span style={{ display: 'block', color: C.textHi, fontWeight: 700 }}>{dayLabel(day, today)}</span>
                {plural(active.length, 'booking')} · {plural(guests, 'guest')}
              </span>
            </div>

            <div className="c29-scroll" style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {error && <ErrorNote error={error} onRetry={load} />}
              {!data && !error && Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={96} />)}
              {data && active.length === 0 && (
                <Empty icon={<IconCalendar size={28} />} title="No reservations this day"
                  body={canWrite ? 'Guest on the phone? Tap New reservation.' : 'Bookings made online or by phone appear here.'} />
              )}
              {active.map((r) => (
                <Row key={r.id} r={r} busy={busyId === r.id} canWrite={canWrite} onAction={onAction} />
              ))}
              {closed.length > 0 && (
                <button type="button" onClick={() => setShowClosed((v) => !v)}
                  style={{ minHeight: 48, border: 'none', background: 'transparent', color: C.textLo, fontSize: 14, fontWeight: 600, textAlign: 'left', padding: '0 4px' }}>
                  {showClosed ? 'Hide' : 'Show'} cancelled & no-show ({closed.length})
                </button>
              )}
              {showClosed && closed.map((r) => (
                <Row key={r.id} r={r} busy={busyId === r.id} canWrite={canWrite} onAction={onAction} dim />
              ))}
            </div>
          </>
        )}
      </div>

      <Modal open={!!ask} onClose={() => setAsk(null)} label="Confirm" width={460} dark>
        {ask && (
          <>
            <ModalHead dark title={ask.action.label} sub={ask.action.ask} onClose={() => setAsk(null)} />
            <ModalFoot dark>
              <Btn kind="dark" height={52} onClick={() => setAsk(null)}>Back</Btn>
              <Btn kind={ask.action.tone === 'danger' ? 'danger' : 'orange'} height={52} onClick={() => run(ask.r, ask.action)}>{ask.action.label}</Btn>
            </ModalFoot>
          </>
        )}
      </Modal>
    </div>
  );
}

function Row({ r, busy, canWrite, onAction, dim }) {
  const chip = STATUS_CHIP[r.status] || STATUS_CHIP.completed;
  const [clock, ampm] = hhmm12(r.time).split(' ');
  const actions = canWrite ? reservationActions(r) : [];
  return (
    <div data-testid={`dining-reservation-${r.id}`}
      style={{ borderRadius: 16, background: C.panel2, border: `1px solid ${C.line2}`, display: 'flex', overflow: 'hidden', opacity: dim ? 0.7 : 1 }}>
      <div style={{ width: 84, flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.tile, borderRight: `1px solid ${C.line}` }}>
        <span className="c29-tight" style={{ fontSize: 22, fontWeight: 800, color: C.textHi, fontFamily: FONT_TIGHT }}>{clock}</span>
        <span className="c29-caps" style={{ color: C.textLo }}>{ampm}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: C.textHi }}>{r.customer_name}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14, color: C.textMid }}><IconUsers size={16} />{r.party_size}</span>
          <Chip bg={chip.bg} fg={chip.fg}>{chip.label}</Chip>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: C.textLo }}>Table <b style={{ color: C.textHi, fontSize: 16 }}>{r.table_number || '—'}</b></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: C.textLo }}>
          <span>{r.customer_phone}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{r.source === 'phone' ? <IconPhone size={13} /> : null}{r.source === 'phone' ? `Phone${r.created_by_name ? ` · ${r.created_by_name}` : ''}` : 'Online'}</span>
          {!r.client_verified && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: C.amberHi }}><IconShield size={13} />Unverified</span>}
          {r.confirmation_texted_at && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconCheck size={13} />Texted</span>}
          {r.occasion && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#C4B5FD' }}><IconGift size={13} />{r.occasion}</span>}
        </div>
        {r.allergies && (
          <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: 'rgba(239,68,68,.16)', color: '#FCA5A5', fontSize: 13, fontWeight: 700 }}>
            <IconAlert size={14} />Allergy: {r.allergies}
          </span>
        )}
        {r.special_requests && <span style={{ fontSize: 13, color: C.textMid }}>“{r.special_requests}”</span>}
        {actions.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
            {actions.map((a) => (
              <Btn key={a.label} kind="dark" height={48} busy={busy && a.status === 'seated'} disabled={busy}
                icon={a.edit ? <IconPencil size={16} /> : null} onClick={() => onAction(r, a)}
                style={a.tone === 'seat' ? { background: C.violet, borderColor: C.violetHi, color: '#FFFFFF', fontWeight: 700 }
                  : a.tone === 'primary' ? { background: 'rgba(34,197,94,.16)', borderColor: C.green, color: C.greenText, fontWeight: 700 }
                    : a.tone === 'danger' ? { color: '#FCA5A5' } : undefined}>
                {a.label}
              </Btn>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Caption({ children, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="c29-caps" style={{ color: C.textDim }}>{children}</span>
      {hint && <span style={{ fontSize: 12.5, color: C.textLo }}>{hint}</span>}
    </div>
  );
}

function Pill({ on, onClick, children, disabled, tone = 'orange', style }) {
  const ring = tone === 'violet' ? C.violetHi : C.orange;
  const fg = tone === 'violet' ? '#C4B5FD' : '#FDBA74';
  return (
    <button type="button" className="c29-press" aria-pressed={on} disabled={disabled} onClick={onClick}
      style={{ minHeight: 48, padding: '0 14px', borderRadius: 12, border: on ? `2px solid ${ring}` : `1px solid ${C.line2}`, background: on ? 'rgba(249,115,22,.12)' : C.panel2, color: on ? fg : disabled ? C.textOff : C.text, fontSize: 15, fontWeight: 700, textDecoration: disabled ? 'line-through' : 'none', ...style }}>
      {children}
    </button>
  );
}

function ReservationForm({ client, reservation, defaultDate, today, zone, onBack, onSaved }) {
  const {
    editing, form, set, avail, loadingAvail, slots, freeTables, phone, phoneOk, slotFree, ready, saving, problem, save,
  } = useReservationForm({ client, reservation, defaultDate, onSaved });
  return (
    <>
      <header style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <IconBtn dark size={48} label="Back to the list" onClick={onBack}><IconArrowLeft size={20} /></IconBtn>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="c29-tight" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.textHi }}>{editing ? `Edit ${reservation.confirmation_code}` : 'New reservation'}</h2>
          <span style={{ fontSize: 13, color: C.textLo }}>{editing ? 'Checked against the book like a new booking.' : 'Confirmed as soon as you book.'}</span>
        </div>
      </header>

      <div className="c29-scroll" style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 22 }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Caption>Guest</Caption>
          <input autoFocus placeholder="Guest name" value={form.name} onChange={(e) => set({ name: e.target.value })} style={darkInput} aria-label="Guest name" />
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={form.code} onChange={(e) => set({ code: e.target.value })} aria-label="Country code" style={{ ...darkInput, width: 130, flex: 'none' }}>
              {COUNTRY_CODES.map(([c, label]) => <option key={c} value={c}>{c} {label}</option>)}
            </select>
            <input type="tel" inputMode="tel" placeholder="(555) 867-5309" value={form.phone} onChange={(e) => set({ phone: e.target.value })} style={darkInput} aria-label="Phone" />
          </div>
          {form.phone && !phoneOk && <span style={{ fontSize: 13, color: '#FCA5A5' }}>Enter the full phone number.</span>}
          {form.phone && phoneOk && <span style={{ fontSize: 13, color: C.textLo }}>Saved as {phone} · added to the client list as unverified.</span>}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Caption>Party</Caption>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <IconBtn dark size={52} label="One fewer guest" disabled={form.party <= 1} onClick={() => set({ party: form.party - 1, tableId: null })}><IconMinus size={18} /></IconBtn>
            <span className="c29-tight" style={{ width: 48, textAlign: 'center', fontSize: 26, fontWeight: 800, color: C.textHi }}>{form.party}</span>
            <IconBtn dark size={52} label="One more guest" disabled={form.party >= 99} onClick={() => set({ party: form.party + 1, tableId: null })}><IconPlus size={18} /></IconBtn>
            {[2, 4, 6, 8].map((n) => <Pill key={n} on={form.party === n} onClick={() => set({ party: n, tableId: null })} style={{ width: 56 }}>{n}</Pill>)}
          </div>
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Caption hint={zone ? `Restaurant time (${zone})` : null}>When</Caption>
          <input type="date" min={today || undefined} value={form.date} onChange={(e) => set({ date: e.target.value, time: '', tableId: null })} style={{ ...darkInput, width: 200 }} aria-label="Date" />
          {loadingAvail && !avail && <Skeleton height={104} />}
          {avail && !avail.is_open && <span style={{ fontSize: 14, color: C.amberHi }}>{avail.message || 'The restaurant is closed that day.'}</span>}
          {avail && avail.is_open && slots.length === 0 && <span style={{ fontSize: 14, color: C.textLo }}>No start times left on this day.</span>}
          {avail && avail.is_open && slots.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
              {slots.map((s) => {
                const full = s.tables_available === 0;
                return (
                  <Pill key={s.time} on={form.time === s.time} disabled={full && form.time !== s.time} onClick={() => set({ time: s.time })}>
                    {hhmm12(s.time)}
                  </Pill>
                );
              })}
            </div>
          )}
          {avail?.sitting_minutes && form.time && <span style={{ fontSize: 13, color: C.textLo }}>Holds the table {avail.sitting_minutes} minutes.</span>}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Caption hint="Only tables that seat the party and are free for this sitting.">Table</Caption>
          {!form.time ? <span style={{ fontSize: 14, color: C.textLo }}>Pick a time first.</span> : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Pill on={form.tableId == null} onClick={() => set({ tableId: null })}>Best free table</Pill>
              {freeTables.map((t) => (
                <Pill key={t.id} on={form.tableId === t.id} onClick={() => set({ tableId: t.id })}>
                  {t.table_number}<span style={{ fontSize: 12, color: C.textLo, marginLeft: 6, fontWeight: 600 }}>{t.capacity}p{t.section ? ` · ${t.section}` : ''}</span>
                </Pill>
              ))}
              {avail && freeTables.length === 0 && <span style={{ fontSize: 14, color: '#FCA5A5', width: '100%' }}>No table is free for this party at {hhmm12(form.time)}.</span>}
            </div>
          )}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Caption>Details</Caption>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {OCCASIONS.map((o) => <Pill key={o} tone="violet" on={form.occasion === o} onClick={() => set({ occasion: form.occasion === o ? '' : o })}>{o}</Pill>)}
          </div>
          <input placeholder="Occasion (or type your own)" maxLength={50} value={form.occasion} onChange={(e) => set({ occasion: e.target.value })} style={darkInput} aria-label="Occasion" />
          <input placeholder="Allergies (e.g. peanuts, shellfish)" maxLength={500} value={form.allergies} onChange={(e) => set({ allergies: e.target.value })} style={{ ...darkInput, borderColor: form.allergies ? C.redHi : C.line2 }} aria-label="Allergies" />
          <textarea rows={3} maxLength={1000} placeholder="Notes (high chair, window seat, running late…)" value={form.notes} onChange={(e) => set({ notes: e.target.value })} style={{ ...darkInput, height: 'auto', padding: 12, resize: 'vertical' }} aria-label="Notes" />
        </section>

        <button type="button" role="checkbox" aria-checked={form.text} onClick={() => set({ text: !form.text })}
          style={{ minHeight: 64, borderRadius: 14, border: `1.5px solid ${form.text ? C.orange : C.line2}`, background: form.text ? 'rgba(249,115,22,.08)' : C.panel2, display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', textAlign: 'left' }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, flex: 'none', border: `2px solid ${form.text ? C.orange : C.textOff}`, background: form.text ? C.orange : 'transparent', color: C.orangeDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {form.text && <IconCheck size={16} />}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.textHi }}>Text confirmation to guest</span>
            <span style={{ fontSize: 12.5, color: C.textLo }}>Only if the guest agreed on the call. The same text online bookings get, with the confirmation code.</span>
          </span>
        </button>
      </div>

      <footer style={{ padding: '14px 20px 18px', borderTop: `1px solid ${C.line}`, background: C.bar, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {problem && <ErrorNote error={problem} compact />}
        {!slotFree && <span style={{ fontSize: 14, color: C.amberHi }}>That time has no free table for this party any more.</span>}
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn kind="dark" height={56} onClick={onBack} disabled={saving}>Back</Btn>
          <Btn kind="orange" height={56} full busy={saving} disabled={!ready} onClick={save}>
            {editing ? 'Save changes' : `Book ${plural(form.party, 'guest')}${form.time ? ` at ${hhmm12(form.time)}` : ''}`}
          </Btn>
        </div>
      </footer>
    </>
  );
}
